import type { CircleMarker, Map as LeafletMap } from 'leaflet';
import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../../services/api.js';
import { mytClock, mytDateTime } from '../../utils/mytTime.js';
import type { PickupZone } from './DrivingAreaCard.js';

/**
 * 405 (#315, mock-up A1–A3): where the customer's car is, from Cartrack. Shown only while the booking page says the
 * position may be read (`car.live_position`); the platform decides that (`mayTrackVehicle`), never this file.
 *
 * - Asks `GET /bookings/:id/position` on open and every 60 s while the page is visible (spec FR-009).
 * - A 404 means the window has closed: it stops asking and shows nothing (353's re-read then drops the flag).
 * - No position: a plain sentence, plus the pasted tracking link when the car has one (FR-010).
 * - Leaflet is loaded only when there is a point to draw, so the app's first load is unchanged (research R10).
 */
export const REFRESH_MS = 60_000;

type Position =
  | { state: 'live'; latitude: number; longitude: number; fix_at: string | null; stale: boolean }
  | { state: 'unavailable' };

type View = { state: 'loading' } | { state: 'gone' } | Position;

const mapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

export function CarPositionMap({
  bookingId,
  fallbackUrl,
  zone,
}: {
  bookingId: string;
  fallbackUrl?: string | null;
  /** 416 (#1431, mock-up screen 6): the driving area, drawn around the car for the customer's own reference. */
  zone?: PickupZone | null;
}) {
  const [view, setView] = useState<View>({ state: 'loading' });
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<LeafletMap | null>(null);
  const marker = useRef<CircleMarker | null>(null);

  useEffect(() => {
    let stopped = false;
    const read = async () => {
      try {
        const p = await api.get<Position>(`/bookings/${bookingId}/position`);
        if (!stopped) setView(p.state === 'live' ? p : { state: 'unavailable' });
      } catch (err) {
        if (stopped) return;
        if ((err as { status?: number }).status === 404) {
          stopped = true;
          clearInterval(timer);
          setView({ state: 'gone' });
          return;
        }
        setView({ state: 'unavailable' });
      }
    };
    void read();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void read();
    }, REFRESH_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [bookingId]);

  const live = view.state === 'live' ? view : null;

  // Draw or move the point; the map is made once, on the first position. Keyed on the point's values, not the object,
  // so a re-read that returns the same point does not redraw.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the point's primitives are the whole input
  useEffect(() => {
    if (!live || !container.current) return;
    let cancelled = false;
    void (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !container.current) return;
      const at: [number, number] = [live.latitude, live.longitude];
      if (!map.current) {
        map.current = L.map(container.current, { attributionControl: true });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map.current);
        // 416: the driving area under the car (outline, pickup point).
        if (zone) {
          L.polygon(
            zone.polygon.map(([lng, lat]) => [lat, lng] as [number, number]),
            {
              color: '#ffd800',
              weight: 2,
              dashArray: '6 4',
              fillColor: '#ffd800',
              fillOpacity: 0.12,
            },
          ).addTo(map.current);
          if (zone.pickup) {
            L.circleMarker([zone.pickup[1], zone.pickup[0]], {
              radius: 6,
              color: '#ffffff',
              weight: 2,
              fillColor: '#0070c0',
              fillOpacity: 1,
            }).addTo(map.current);
          }
        }
        marker.current = L.circleMarker(at, {
          radius: 9,
          color: live.stale ? '#8a8a8a' : '#ffd800',
          fillColor: live.stale ? '#8a8a8a' : '#ffd800',
          fillOpacity: 1,
          weight: 6,
          opacity: 0.35,
        }).addTo(map.current);
      } else {
        marker.current?.setLatLng(at);
      }
      map.current.setView(at, 15);
    })();
    return () => {
      cancelled = true;
    };
  }, [live?.latitude, live?.longitude, live?.stale]);

  // Tear the map down when the card goes.
  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
      marker.current = null;
    },
    [],
  );

  if (view.state === 'gone' || view.state === 'loading') return null;

  return (
    <div className="mt-3 bg-surface-card rounded-xl p-4" data-testid="car-position">
      <p className="font-semibold text-text mb-2">
        {zone ? 'Your driving area' : 'Where your car is'}
      </p>
      {zone && (
        <p data-testid="car-position-zone-line" className="-mt-1 mb-2 text-xs text-text-muted">
          {zone.location_name} · drive inside the outline to avoid per-km charges
        </p>
      )}
      {live ? (
        <>
          {/* `isolate`: Leaflet's panes sit at z-index 400+, above the fixed bottom nav (z-50) and the dialogs. */}
          <div
            ref={container}
            className="isolate h-40 w-full rounded-lg overflow-hidden bg-surface-mid"
            aria-label="Map showing where the car is"
          />
          <p
            data-testid="car-position-meta"
            className={`mt-2 text-xs ${live.stale ? 'text-warning' : 'text-text-muted'}`}
          >
            {live.stale
              ? `Last seen ${live.fix_at ? mytDateTime(live.fix_at) : 'a while ago'}`
              : `Updated ${live.fix_at ? mytClock(live.fix_at) : 'just now'} · refreshes every minute`}
          </p>
          <a
            href={mapsUrl(live.latitude, live.longitude)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block rounded-full border border-border py-2.5 text-center text-sm font-semibold text-text"
          >
            Open in Maps
          </a>
        </>
      ) : (
        <>
          <p data-testid="car-position-unavailable" className="text-sm text-text-muted">
            The car's position is not available right now. We will try again in a minute.
          </p>
          {fallbackUrl && (
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block text-center text-primary font-semibold underline"
            >
              See where the car is
            </a>
          )}
        </>
      )}
    </div>
  );
}
